import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import { useNotifications } from '../../contexts/NotificationsContext';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { LoadingSpinner } from '../LoadingSpinner';
import { createReturn, repeatShipment } from '../../utils/largeFormikUpdates';
import { getLocalDateTimeString } from '../../utils/localDateTime';

const initialFormFields = {
    startDate: getLocalDateTimeString().slice(0, 10),
    endDate: getLocalDateTimeString().slice(0, 10),
    filterBy: ''
}

export const ShipmentHistoryModal = ({ formik, show, handleClose, updateHideLookup, resetTabs }) => {
    const { addNotification } = useNotifications();
    const [shipments, setShipments] = useState([]);
    const [filteredShipments, setFilteredShipments] = useState([]);
    const [formFields, setFormFields] = useState(initialFormFields);
    const [historyFetchLoading, setHistoryFetchLoading] = useState(false);
    const [selectedShipmentId, setSelectedShipmentId] = useState(null);
    const [selectedShipment, setSelectedShipment] = useState({});

    useEffect(() => {
        if (show === true && shipments.length === 0 && formFields.startDate && formFields.endDate) {
            getShipmentHistoryData();
        }
    }, [show]);

    const closeModal = () => {
        handleClose();
        setSelectedShipmentId(null);
        setSelectedShipment({});
    }

    const getShipmentHistoryData = async () => {
        setHistoryFetchLoading(true);
        if (selectedShipmentId) {
            setSelectedShipmentId(null);
            setSelectedShipment({});
        }
        if (!formFields.startDate || !formFields.endDate) {
            setHistoryFetchLoading(false);
            return alert('Start date and end date are required to fetch shipments.');
        }
        try {
            const url = `${process.env.REACT_APP_BASE_URL}/aptify/shipments/${formFields.startDate}/${formFields.endDate}`;
            const result = await axios.get(url);
            const shipmentsData = result.data.shipments;
            setShipments(shipmentsData);
            setFilteredShipments(shipmentsData);
            setFormFields({ ...formFields, filterBy: '' })
            setHistoryFetchLoading(false);
        } catch (err) {
            setHistoryFetchLoading(false);
            addNotification('Error fetching shipment history.', err.message || err, 'danger');
            console.log('Error fetching shipment history: ' + err);
        }
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormFields({ ...formFields, [name]: value });
    }
    const handleRadioChange = (e) => {
        setSelectedShipmentId(+e.target.value);
        const shipmentObj = shipments.find((s) => s.ID === +e.target.value);
        setSelectedShipment(shipmentObj);
    }
    const handleFilterChange = (e) => {
        handleChange(e);
        setSelectedShipmentId(null);
        setSelectedShipment({});
        const { value } = e.target;
        switch (value) {
            case 'DOMESTIC':
                setFilteredShipments(shipments.filter((s) => s.ShipToCountry === 'US'));
                return;
            case 'INTERNATIONAL':
                setFilteredShipments(shipments.filter((s) => s.ShipToCountry !== 'US'));
                return;
            case 'RETURNS':
                setFilteredShipments(shipments.filter((s) => s.FedEx_IsReturn === true));
                return;
            default:
                setFilteredShipments(shipments);
                return;
        }
    }

    const handleRepeatShipment = () => {
        let associatedReturnShipment;
        if (selectedShipment.includeReturnLabel) {
            associatedReturnShipment = shipments.filter((s) => s.FedEx_IsReturn === true)
                .find((s) => s.linkedOutboundTrackingNo === selectedShipment.FedEx_MasterTrackingNo);
        }
        repeatShipment(formik, selectedShipment, associatedReturnShipment);
        updateHideLookup(true);
        if (selectedShipment.FedEx_IsReturn) {
            resetTabs('returnShipment');
        } else {
            resetTabs('shipmentDetails');
        }
        closeModal();
    }

    const handleCreateReturn = () => {
        createReturn(formik, selectedShipment);
        updateHideLookup(true);
        resetTabs('returnShipment');
        closeModal();
    }

    const viewDetails = () => {
        console.log('view details function...');
    }

    return (
        <Modal show={show} onHide={closeModal} backdrop='static'>
            <Modal.Header closeButton>
                <Modal.Title>Shipment history</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className='history-search'>
                    <p>Search by date:</p>
                    <Form.Group>
                        <Form.Label>From</Form.Label>
                        <Form.Control
                            type='date'
                            name='startDate'
                            value={formFields.startDate}
                            onChange={handleChange}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>To</Form.Label>
                        <Form.Control
                            type='date'
                            name='endDate'
                            value={formFields.endDate}
                            onChange={handleChange}
                        />
                    </Form.Group>
                    <Button onClick={getShipmentHistoryData} variant='secondary'>
                        {historyFetchLoading ? <LoadingSpinner /> : 'Search'}
                    </Button>
                </div>
                <div className='history-search'>
                    <p>Filter:</p>
                    <Form.Select
                        className='filter-ship-history'
                        name='filterBy'
                        value={formFields.filterBy}
                        onChange={handleFilterChange}
                    >
                        <option value=''>All</option>
                        <option value='DOMESTIC'>Domestic</option>
                        <option value='INTERNATIONAL'>International</option>
                        <option value='RETURNS'>Returns</option>
                    </Form.Select>
                </div>
                <div className='ship-history-table-wrapper'>
                    <Table striped bordered hover>
                        <thead className='text-nowrap'>
                            <tr>
                                <th></th>
                                <th>Carrier</th>
                                <th>Tracking #</th>
                                <th>Master Trk #</th>
                                <th>Ship Date</th>
                                <th>Service type</th>
                                <th>Package type</th>
                                <th>Weight</th>
                                <th>Recipient ID</th>
                                <th>Recipient Contact Name</th>
                                <th>Recipient Company</th>
                                <th>Recipient City</th>
                                <th>Recipient State/Province</th>
                                <th>Zip</th>
                                <th>Invoice #</th>
                                <th>P.O. #</th>
                                <th>Customer Reference #</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                filteredShipments.map(s => (
                                    <tr key={s.ID}>
                                        <td>
                                            <Form.Check
                                                type='radio'
                                                name=''
                                                value={s.ID}
                                                onChange={handleRadioChange}
                                                checked={selectedShipmentId === s.ID}
                                            />
                                        </td>
                                        <td>{s.FedEx_Carrier}</td>
                                        <td>{s.TrackingNo}</td>
                                        <td>{s.FedEx_MasterTrackingNo}</td>
                                        <td>{new Date(s.ShipDate).toLocaleDateString('en-US')}</td>
                                        <td>{s.FedEx_ServiceType}</td>
                                        <td>{s.FedEx_PackageType}</td>
                                        <td>{s.FedEx_Weight}</td>
                                        <td>{s.FedEx_IsReturn ? s.FedEx_ShipperID : ''}</td>
                                        <td>{s.ShipToName}</td>
                                        <td>{s.ShipToCompany}</td>
                                        <td>{s.ShipToCity}</td>
                                        <td>{s.ShipToState}</td>
                                        <td>{s.ShipToZipCode}</td>
                                        <td>{s.FedEx_InvoiceNum}</td>
                                        <td>{s.FedEx_PoNum}</td>
                                        <td>{s.FedEx_DepartmentNotesName}</td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </Table>
                </div>
            </Modal.Body>
            <Modal.Footer className='history-footer'>
                <ButtonGroup>
                    <Button onClick={handleRepeatShipment} variant='secondary' disabled={!selectedShipmentId}>Repeat</Button>
                    <Button onClick={handleCreateReturn} variant='secondary' disabled={!selectedShipmentId || selectedShipment.FedEx_IsReturn}>Create return</Button>
                    {/* TODO - implement below button */}
                    {false && <Button onClick={viewDetails} variant='secondary' disabled={!selectedShipmentId}>View shipment detail</Button>}
                </ButtonGroup>
                <div>
                    <Button onClick={closeModal} variant='secondary'>Close</Button>
                </div>
            </Modal.Footer>
        </Modal>
    )
}
